import NumberFormat, {
  type InputAttributes,
  type NumberFormatProps,
  type NumberFormatValues,
} from "react-number-format";
import { useCallback, useMemo } from "react";

import { Input2, type InputVariantProps } from "../atoms/Input";

export type PhoneNumberInputProps = InputVariantProps &
  Omit<NumberFormatProps, "size"> & {
    /** Called with raw unmasked digits (e.g. "5551234567") instead of the formatted string */
    onChange?: (value: string) => void;
  };

type CustomInputProps =
  | NumberFormatProps<InputAttributes>
  | Readonly<NumberFormatProps<InputAttributes>>;

const PHONE_NUMBER_FORMAT = "(###) ###-####";

export function PhoneNumberInput({ onChange, size, variant, ...props }: PhoneNumberInputProps) {
  const CustomInput = useMemo(
    () =>
      function CustomInput(inputProps: CustomInputProps) {
        return <Input2 {...inputProps} size={size} variant={variant} />;
      },
    [size, variant],
  );

  /**  react-number-format's onChange fires with the formatted string; use
   * onValueChange instead to forward raw digits so consumers (e.g. RHF
   * field.onChange) receive the unmasked value that schema validation expects.*/
  const handleValueChange = useCallback(
    (values: NumberFormatValues) => {
      onChange?.(values.value);
    },
    [onChange],
  );

  return (
    <NumberFormat
      customInput={CustomInput}
      format={PHONE_NUMBER_FORMAT}
      onValueChange={handleValueChange}
      type="text"
      {...props}
    />
  );
}
